import { useEffect, useState } from 'react';

import { Visibility } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { useDesignerApp } from '../../../UserInterfaceDesigner/Contexts/DesignerAppContext';
import { Bundle } from '../../../UserInterfaceDesigner/Models/Bundle';
import { ViewModel } from '../../../UserInterfaceDesigner/Models/ViewModel';
import { ViewRefModel } from '../../Models/ViewRefModel';
import ListWithFilter from '../ListWithFilter';

type ViewInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	viewRef: ViewRefModel | undefined;
	onViewRefChange: (viewRef: ViewRefModel | undefined) => void;
	/** Bundle with views (from context or prop) */
	bundle?: Bundle | null;
};

export function ViewInputDialog({ open, setOpen, viewRef, onViewRefChange, bundle }: ViewInputDialogProps) {
	const [selectedId, setSelectedId] = useState<string | undefined>(viewRef?.Id);
	const designerBundle = useDesignerApp();

	// Use provided bundle, or fall back to designer context bundle
	const activeBundle = bundle ?? designerBundle.bundle;
	const views: ViewModel[] = activeBundle?.Views || [];

	useEffect(() => {
		if (open) {
			setSelectedId(viewRef?.Id);
		}
	}, [open, viewRef]);

	const handleSelect = (view: ViewModel) => {
		setSelectedId(view.Id);
	};

	const handleSave = () => {
		if (selectedId) {
			const view = views.find((v: ViewModel) => v.Id === selectedId);
			onViewRefChange({
				Id: selectedId,
				Name: view?.Title,
			});
		}
		setOpen(false);
	};

	return (
		<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
			<DialogTitle>Select View</DialogTitle>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
				{views.length > 0 ? (
					<ListWithFilter
						items={views}
						selectedItemId={selectedId}
						onSelected={handleSelect}
						placeholder="Search views"
						getDisplayName={(view) => view.Title}
						getGroup={(view) => view.Menu?.SectionTitle}
						getKey={(view) => view.Id}
						icon={() => <Visibility fontSize="small" />}
						settingsKey="viewInputDialog"
						sx={{ flex: 1, minHeight: 0 }}
					/>
				) : (
					<Box sx={{ p: 2, textAlign: 'center' }}>
						<Typography variant="body2" color="text.secondary">
							No views available in this bundle.
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={() => setOpen(false)}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" disabled={!selectedId}>
					Select
				</Button>
			</DialogActions>
		</Dialog>
	);
}
